import I18n from 'i18n-js';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector, useRoute, useRoutePrice } from '../../../hooks';
import { setBooking } from '../../../redux/slices/booking.slice';
import { Booking, SearchParams } from '../../../shared/interface';
import './BookingCard.scss';

export interface BookingCardProps {
  routeName: string;
  booking: Booking;
}

export const BookingCard = ({ routeName, booking }: BookingCardProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const searchParams = useAppSelector(state => state.boatTripsSearch.data.searchParams) as SearchParams;

  const { departureTrip, returnTrip } = booking;
  const [departureDuration, setDepartureDuration] = useState<string>('');
  const [returnDuration, setReturnDuration] = useState<string>('');
  const [departureFullyBooked, setDepartureFullyBooked] = useState<boolean>(false);
  const [returnFullyBooked, setReturnFullyBooked] = useState<boolean>(false);

  const { routeId } = useParams();
  const route = useRoute(routeId!);
  const price = useRoutePrice(booking);

  useEffect(() => {
    const departureDuration = departureTrip.arrivalSchedule.arrival!.diff(departureTrip.departureSchedule.departure!).shiftTo('hours', 'minutes');
    const returnDuration = returnTrip?.arrivalSchedule.arrival!.diff(returnTrip.departureSchedule.departure!).shiftTo('hours', 'minutes');

    setDepartureDuration(`${departureDuration.hours}${I18n.t('::Units:Hour')} ${departureDuration.minutes}${I18n.t('::Units:Minute')}`);

    if (returnDuration) {
      setReturnDuration(`${returnDuration.hours}${I18n.t('::Units:Hour')} ${returnDuration.minutes}${I18n.t('::Units:Minute')}`);
    }
  }, [departureTrip, returnTrip]);

  useEffect(() => {
    if (route) {
      const totalBooking = searchParams.numberOfAdults + searchParams.numberOfChildren;
      const availableCapacityDeparture = route.capacity - booking.departureTrip.bookedCapacity;
      const availableCapacityReturn = route.capacity - (booking.returnTrip?.bookedCapacity ?? 0);

      setDepartureFullyBooked(totalBooking > availableCapacityDeparture);
      setReturnFullyBooked(totalBooking > availableCapacityReturn);
    }
  }, [searchParams, booking, route]);

  const book = async () => {
    dispatch(setBooking(booking));

    navigate('/checkout/order');
  }

  return (
    <Card className='booking-card my-3 shadow-sm'>
      <Card.Header className='d-flex justify-content-between border-0 position-relative overflow-hidden' >
        <div className="d-flex align-items-center position-relative">
          <h5 className="mb-0 fw-bold">{routeName}</h5>
        </div>
        <div className="d-flex align-items-center position-relative">
          
          <h5 className="mb-0 fw-bold text-success">{price}kr</h5>
        </div>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col xs={'12'} md={'10'}>
            <div className="booking-details">
              {/* Departure Trip */}
              <div className={`trip-row p-3 border-bottom ${departureFullyBooked ? 'trip-fully-booked' : ''}`}>
                {/* Mobile Layout */}
                <div className="d-md-none">
                  <div className="trip-field mb-2">
                    <div className="field-label fw-bold text-muted small">
                      <i className="bi bi-geo-alt me-2 text-primary"></i>{I18n.t('::Route')}
                    </div>
                    <div className="field-value fw-bold">
                      <i className="bi bi-pin-map me-2 text-success"></i>
                      {departureTrip.departureHarbourName} 
                      <i className="bi bi-arrow-right mx-2 text-primary"></i>
                      <i className="bi bi-pin-map-fill me-2 text-danger"></i>
                      {departureTrip.arrivalHarbourName}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="trip-field mb-2">
                        <div className="field-label fw-bold text-muted small">
                          <i className="bi bi-clock me-2 text-info"></i>{I18n.t('::Departure')}
                        </div>
                        <div className="field-value">{departureTrip.departureSchedule.departure?.toFormat('HH:mm')}</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="trip-field mb-2">
                        <div className="field-label fw-bold text-muted small">
                          <i className="bi bi-clock-fill me-2 text-warning"></i>{I18n.t('::Arrival2')}
                        </div>
                        <div className="field-value">{departureTrip.arrivalSchedule.arrival?.toFormat('HH:mm')}</div>
                      </div>
                    </div>
                  </div>
                  <div className="trip-field mb-2">
                    <div className="field-label fw-bold text-muted small">
                      <i className="bi bi-hourglass-split me-2 text-secondary"></i>{I18n.t('::Duration')}
                    </div>
                    <div className="field-value text-muted fst-italic">{departureDuration}</div>
                  </div>
                  {departureFullyBooked && (
                    <div className="alert alert-danger mb-0 mt-2">
                      <small>{route?.fullyBookedMessage ?? I18n.t('::FullyBooked')}</small>
                    </div>
                  )}
                </div>

                {/* Desktop Layout */}
                <div className="d-none d-md-flex align-items-center">
                  <div className="col-md-3 fw-bold">
                    <i className="bi bi-geo-alt me-2 text-success"></i>{departureTrip.departureHarbourName}
                  </div>
                  <div className="col-md-2">
                    <i className="bi bi-play-circle me-2 text-info"></i>{departureTrip.departureSchedule.departure?.toFormat('HH:mm')}
                  </div>
                  <div className="col-md-1 text-center">
                    <i className="bi bi-arrow-right text-primary"></i>
                  </div>
                  <div className="col-md-3 fw-bold">
                    <i className="bi bi-geo-alt-fill me-2 text-danger"></i>{departureTrip.arrivalHarbourName}
                  </div>
                  <div className="col-md-2">
                    <i className="bi bi-stop-circle me-2 text-warning"></i>{departureTrip.arrivalSchedule.arrival?.toFormat('HH:mm')}
                  </div>
                  <div className="col-md-1 text-muted fst-italic small">
                    <i className="bi bi-hourglass-split me-1 text-secondary"></i>{departureDuration}
                  </div>
                  {departureFullyBooked && (
                    <div className="col-md-12 mt-2">
                      <div className="alert alert-danger mb-0">
                        <small>{route?.fullyBookedMessage ?? I18n.t('::FullyBooked')}</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Return Trip */}
              {returnTrip && (
                <div className={`trip-row p-3 ${returnFullyBooked ? 'trip-fully-booked' : ''}`}>
                  {/* Mobile Layout */}
                  <div className="d-md-none">
                    <div className="trip-field mb-2">
                      <div className="field-label fw-bold text-muted small">
                        <i className="bi bi-arrow-return-left me-2 text-primary"></i>{I18n.t('::Return')}
                      </div>
                      <div className="field-value fw-bold">
                        <i className="bi bi-pin-map me-2 text-success"></i>
                        {returnTrip.departureHarbourName} 
                        <i className="bi bi-arrow-right mx-2 text-primary"></i>
                        <i className="bi bi-pin-map-fill me-2 text-danger"></i>
                        {returnTrip.arrivalHarbourName}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <div className="trip-field mb-2">
                          <div className="field-label fw-bold text-muted small">
                            <i className="bi bi-clock me-2 text-info"></i>{I18n.t('::Departure')}
                          </div>
                          <div className="field-value">{returnTrip.departureSchedule.departure?.toFormat('HH:mm')}</div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="trip-field mb-2">
                          <div className="field-label fw-bold text-muted small">
                            <i className="bi bi-clock-fill me-2 text-warning"></i>{I18n.t('::Arrival2')}
                          </div>
                          <div className="field-value">{returnTrip.arrivalSchedule.arrival?.toFormat('HH:mm')}</div>
                        </div>
                      </div>
                    </div>
                    <div className="trip-field mb-2">
                      <div className="field-label fw-bold text-muted small">
                        <i className="bi bi-hourglass-split me-2 text-secondary"></i>{I18n.t('::Duration')}
                      </div>
                      <div className="field-value text-muted fst-italic">{returnDuration}</div>
                    </div>
                    {returnFullyBooked && (
                      <div className="alert alert-danger mb-0 mt-2">
                        <small>{route?.fullyBookedMessage ?? I18n.t('::FullyBooked')}</small>
                      </div>
                    )}
                  </div>

                  {/* Desktop Layout */}
                  <div className="d-none d-md-flex align-items-center">
                    <div className="col-md-3 fw-bold">
                      <i className="bi bi-geo-alt me-2 text-success"></i>{returnTrip.departureHarbourName}
                    </div>
                    <div className="col-md-2">
                      <i className="bi bi-play-circle me-2 text-info"></i>{returnTrip.departureSchedule.departure?.toFormat('HH:mm')}
                    </div>
                    <div className="col-md-1 text-center">
                      <i className="bi bi-arrow-right text-primary"></i>
                    </div>
                    <div className="col-md-3 fw-bold">
                      <i className="bi bi-geo-alt-fill me-2 text-danger"></i>{returnTrip.arrivalHarbourName}
                    </div>
                    <div className="col-md-2">
                      <i className="bi bi-stop-circle me-2 text-warning"></i>{returnTrip.arrivalSchedule.arrival?.toFormat('HH:mm')}
                    </div>
                    <div className="col-md-1 text-muted fst-italic small">
                      <i className="bi bi-hourglass-split me-1 text-secondary"></i>{returnDuration}
                    </div>
                    {returnFullyBooked && (
                      <div className="col-md-12 mt-2">
                        <div className="alert alert-danger mb-0">
                          <small>{route?.fullyBookedMessage ?? I18n.t('::FullyBooked')}</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Col>
          <Col className='d-flex justify-content-end' xs={'12'} md={'2'}>
            <Button
              className='ms-auto mb-auto'
              size={'sm'}
              variant={'success'}
              disabled={departureFullyBooked || returnFullyBooked}
              onClick={book}>
              <i className="bi bi-ticket-perforated me-2"></i>
              {I18n.t('::Book')}
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}